import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppStore } from "@/core/store/useAppStore";
import { AdminMediaCard } from "@/components/MediaCard";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { MediaStage } from "@/core/types/mediaTypes";
import { resolveReviewStage } from "@/domains/LibraryView/reviewStage";
import { useReviewUrlState } from "@/core/navigation/useReviewUrlState";
import { ShareWithClientModal } from "@/domains/PostCreator/ShareWithClientModal";
import { LibraryLayout, ContentType } from "./LibraryLayout";
import { DualFormatEditor } from "@/components/ImageEditor/DualFormatEditor";
import { AIImageEditorModal } from "@/components/ImageEditor/AIImageEditorModal";
import { Modal } from "@/components/ui/Modal";
import { PhotoReviewModal } from "./PhotoReviewModal";
import { ImagePreviewModal } from "./ImagePreviewModal";

export const ImageLibrary: React.FC = () => {
  const { selectedClient } = useAppStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [editingImage, setEditingImage] = useState<MediaItem | null>(null);
  const [aiEditingImage, setAiEditingImage] = useState<MediaItem | null>(null);
  const [previewImage, setPreviewImage] = useState<MediaItem | null>(null);
  const review = useReviewUrlState();

  const handleContentTypeChange = (contentType: ContentType) => {
    const pathMap: Record<ContentType, string> = {
      images: "../photos",
      "post-videos": "../post-videos",
      stories: "../stories",
      reels: "../reels",
      videos: "../videos",
      audio: "../audio",
      assets: "../assets",
    };
    navigate(pathMap[contentType]);
  };

  const handleEnterReviewMode = (selectedIds: number[]) => {
    // Start from the first selected item (or the beginning if none selected).
    review.open(selectedIds.length > 0 ? selectedIds[0] : undefined);
  };

  const renderGrid = (
    items: MediaItem[],
    handleStageChange: (updatedItem: MediaItem, oldStage: MediaStage) => void,
    handleItemClick?: (item: MediaItem) => void,
    selectionMode?: {
      isActive: boolean;
      selectedIds: number[];
      onToggle: (id: number, event: React.MouseEvent) => void;
    },
    onAddToCollection?: (mediaItemId: number) => void,
  ) => (
    <>
      {items.map((mediaItem) => (
        <AdminMediaCard
          key={mediaItem.id}
          mediaItem={mediaItem}
          clientId={selectedClient?.id || ""}
          aspectRatio="4:5"
          onStageChange={handleStageChange}
          onMediaItemDeleted={() => {}}
          onEditClick={setEditingImage}
          onClick={() => setPreviewImage(mediaItem)}
          selectionMode={selectionMode}
          onAddToCollection={onAddToCollection}
        />
      ))}
    </>
  );

  return (
    <>
      <LibraryLayout
        contentTypes={["photos"]}
        activeContentType="images"
        onContentTypeChange={handleContentTypeChange}
        renderGrid={renderGrid}
        onShareWithClient={() => setIsShareModalOpen(true)}
        onEnterReviewMode={handleEnterReviewMode}
        extraContent={
          selectedClient && (
            <>
              <ShareWithClientModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                clientId={selectedClient.id}
                clientName={selectedClient.clientName}
                redirectPath={`/client/${selectedClient.id}/review/media`}
              />
              <PhotoReviewModal
                isOpen={review.isOpen}
                onClose={review.close}
                clientId={selectedClient.id}
                stage={resolveReviewStage(searchParams.get("stage"))}
                initialMediaId={review.initialMediaId}
                onCurrentItemChange={review.setCurrentItem}
              />
            </>
          )
        }
      />

      <Modal
        isOpen={!!editingImage}
        onClose={() => setEditingImage(null)}
        showCloseButton={false}
        className="w-full max-w-[95vw] h-[95vh] max-h-[95vh]"
      >
        {editingImage && (
          <DualFormatEditor
            image={editingImage}
            onBack={() => setEditingImage(null)}
            onClose={() => setEditingImage(null)}
          />
        )}
      </Modal>

      {selectedClient && (
        <>
          <ImagePreviewModal
            isOpen={!!previewImage}
            onClose={() => setPreviewImage(null)}
            mediaItem={previewImage}
            clientId={selectedClient.id}
            onEditWithAI={(item) => {
              setPreviewImage(null);
              setAiEditingImage(item);
            }}
          />
          <AIImageEditorModal
            isOpen={!!aiEditingImage}
            onClose={() => setAiEditingImage(null)}
            mediaItem={aiEditingImage}
            clientId={selectedClient.id}
          />
        </>
      )}
    </>
  );
};
